// frontend/packages/business-console/src/pages/BuildDraftReview.jsx
//
// Work item 8.10 - the draft review + approval page (Approve gate v1) and
// the revision loop. After generation deploys a draft, the tenant reviews
// it here: open the live preview, then either approve (-> the founder's
// final delivery/charge gate) or request changes with a short note. A
// request kicks off a regeneration; after the self-serve cap it escalates
// to a human and shows a soft hand-off message.
//
// ⚠ Console builds are NOT runnable in the Claude sandbox, the deploy
// build is the gate and a founder eyeball is owed on this page (the
// 5.10 lesson).
//
// Copy rule: every string here is customer-visible. No em-dashes.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import useDirectUpload from '../hooks/useDirectUpload';
import {
  approveBuildDraft,
  getBuildDraft,
  requestBuildChanges,
} from '../services/buildFunnelService';

// Reference-image attach limits for the "request changes" dialog. Reuses the
// intake proof step's shared Cloudinary direct-upload path; stated up front and
// enforced client-side (backend re-validates host + count).
const IMAGE_MAX = 5;
const IMAGE_MAX_MB = 5;
const IMAGE_MAX_BYTES = IMAGE_MAX_MB * 1024 * 1024;
const IMAGE_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const IMAGE_HINT = `Optional. Attach up to ${IMAGE_MAX} reference photos (JPG, PNG, or WebP, up to ${IMAGE_MAX_MB} MB each).`;

// A build in a failed lane has no preview to review. Without this the page
// showed "the preview link is being set up" forever. The operator alert Task
// is raised server-side; the poll below keeps running, so an operator re-kick
// or a successful retry brings the page back on its own.
function isFailedState(state) {
  return typeof state === 'string' && state.startsWith('failed_');
}

function errMessage(e) {
  return (
    (e && e.response && e.response.data && e.response.data.error) ||
    'Something went wrong. Try again.'
  );
}

export default function BuildDraftReview() {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Honest ack when a revision asks for something the automated editor cannot
  // build (photos, icons, custom design, etc.); routed to the team lane.
  const [notice, setNotice] = useState('');

  // Request-changes dialog.
  const [changesOpen, setChangesOpen] = useState(false);
  const [note, setNote] = useState('');
  // Optional reference images the customer attaches to the change request.
  const { upload, uploading } = useDirectUpload();
  const [images, setImages] = useState([]); // [{ publicId, url }]
  const [imageError, setImageError] = useState('');
  const fileRef = useRef(null);

  const closeChanges = useCallback(() => {
    setChangesOpen(false);
    setNote('');
    setImages([]);
    setImageError('');
  }, []);

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setImageError('');
    for (const file of files) {
      if (images.length >= IMAGE_MAX) {
        setImageError(`You can attach up to ${IMAGE_MAX} images.`);
        break;
      }
      if (file.type && !IMAGE_ACCEPTED_TYPES.includes(file.type)) {
        setImageError(
          `${file.name || 'A file'} is not a supported type. Use JPG, PNG, or WebP.`
        );
        continue;
      }
      if (file.size > IMAGE_MAX_BYTES) {
        setImageError(
          `${file.name || 'A photo'} is too large. The limit is ${IMAGE_MAX_MB} MB.`
        );
        continue;
      }
      try {
        const r = await upload(file);
        setImages((prev) =>
          prev.length >= IMAGE_MAX
            ? prev
            : [...prev, { publicId: r.public_id, url: r.secure_url }]
        );
      } catch {
        setImageError(
          `${file.name || 'A photo'} did not upload. Use a JPG, PNG, or WebP under ${IMAGE_MAX_MB} MB.`
        );
      }
    }
  };
  const removeImage = (i) =>
    setImages((prev) => prev.filter((_, j) => j !== i));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDraft(await getBuildDraft());
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-poll while the draft is still settling, so the customer never has to
  // refresh: the preview link may still be provisioning, or a requested
  // revision may be regenerating. A quiet refetch keeps the last good view on
  // screen until new data arrives, then stops once the draft is ready.
  const refetch = useCallback(async () => {
    try {
      setDraft(await getBuildDraft());
    } catch {
      // Keep the last good draft; the next tick retries.
    }
  }, []);

  const settling = Boolean(
    draft && draft.exists && (!draft.previewUrl || draft.revising)
  );

  useEffect(() => {
    if (!settling) return undefined;
    const id = setInterval(refetch, 5000);
    return () => clearInterval(id);
  }, [settling, refetch]);

  const onApprove = async () => {
    setBusy(true);
    setError('');
    try {
      await approveBuildDraft();
      await load();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmitChanges = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await requestBuildChanges(
        note.trim(),
        images.map((img) => img.url)
      );
      closeChanges();
      // Surface the honest "that needs our team" ack when part of the ask is
      // out of the automated editor's envelope (D24), so it is never a silent
      // no-op.
      setNotice(
        result && result.outOfEnvelope ? result.outOfEnvelope.message : ''
      );
      await load();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 3, px: 2 }}>
      <Typography variant="h5" gutterBottom>
        Review your website
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is the first version of your site. Open the preview, take a look,
        then approve it or tell us what to change.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {notice ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      ) : null}

      {!draft || !draft.exists ? (
        <Alert severity="info" icon={<CircularProgress size={16} />}>
          Your site is not ready to review yet. This page updates automatically
          the moment your preview is built.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {/* Status banners for non-actionable states. */}
          {isFailedState(draft.state) ? (
            <Alert severity="warning">
              We hit a snag building your site. Your answers are saved, our team
              has been alerted, and we are on it. This page updates on its own
              once your preview is ready, and we will reach out if we need
              anything from you.
            </Alert>
          ) : null}
          {draft.revising ? (
            <Alert severity="info" icon={<CircularProgress size={16} />}>
              Thanks. We have your requested changes and are updating your site.
              This page updates automatically when the new version is ready, and
              we will email you too.
            </Alert>
          ) : null}
          {draft.awaitingFounderReview ? (
            <Alert
              severity="success"
              icon={<CheckCircleIcon fontSize="inherit" />}
            >
              You approved this version. Our team is doing a final check before
              it goes live. Nothing more is needed from you right now.
            </Alert>
          ) : null}
          {draft.inRescue ? (
            <Alert severity="info">
              {typeof draft.revisionRounds === 'number' &&
              draft.revisionRounds > 0
                ? `You have asked for changes ${draft.revisionRounds} times, and it should not take this many rounds to get your site right. `
                : 'It should not take this many rounds to get your site just right. '}
              Let us take it from here: we have brought in a member of our team
              to work with you directly, and they will reach out shortly.
            </Alert>
          ) : null}

          {/* Preview. */}
          {isFailedState(draft.state) ? null : (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Your preview
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {draft.previewUrl ? (
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      href={draft.previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Open my site preview
                    </Button>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        height: { xs: 360, md: 520 },
                      }}
                    >
                      <iframe
                        title="Site preview"
                        src={draft.previewUrl}
                        style={{ width: '100%', height: '100%', border: 0 }}
                      />
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="info" icon={<CircularProgress size={16} />}>
                    The preview link is being set up. This page updates
                    automatically when it is ready.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions. */}
          {draft.canApprove || draft.canRequestChanges ? (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Ready to decide?
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<CheckCircleIcon />}
                    disabled={busy || !draft.canApprove}
                    onClick={onApprove}
                  >
                    Approve my site
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<EditOutlinedIcon />}
                    disabled={busy || !draft.canRequestChanges}
                    onClick={() => setChangesOpen(true)}
                  >
                    Request changes
                  </Button>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: 'block' }}
                >
                  Approving sends your site to our team for a final check before
                  it goes live. You will not be charged yet.
                </Typography>
              </CardContent>
            </Card>
          ) : null}
        </Stack>
      )}

      {/* Request-changes note dialog. */}
      <Dialog
        open={changesOpen}
        onClose={() => (busy ? null : closeChanges())}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>What would you like changed?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Tell us in your own words. Be as specific as you like, for example
            &quot;use a warmer color&quot;, &quot;swap the hero photo&quot;, or
            &quot;the services list is missing leak detection&quot;.
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={4}
            maxRows={12}
            fullWidth
            placeholder="Describe the changes you want"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 2000 }}
          />

          {/* Optional reference images ("make it look like this"). */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Add reference photos
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              {IMAGE_HINT}
            </Typography>
            {images.length > 0 ? (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 1 }}
              >
                {images.map((img, i) => (
                  <Box
                    key={img.publicId}
                    sx={{
                      position: 'relative',
                      width: 88,
                      height: 88,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <img
                      src={img.url}
                      alt="Reference"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      aria-label="Remove reference photo"
                      size="small"
                      disabled={busy}
                      onClick={() => removeImage(i)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                uploading ? (
                  <CircularProgress size={16} />
                ) : (
                  <FileUploadOutlinedIcon />
                )
              }
              disabled={busy || uploading || images.length >= IMAGE_MAX}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              {uploading ? 'Uploading…' : 'Upload photos'}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={handleImages}
              style={{ display: 'none' }}
            />
            {imageError ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                {imageError}
              </Alert>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={closeChanges}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || uploading || !note.trim()}
            onClick={onSubmitChanges}
          >
            {busy ? 'Sending…' : 'Send my changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
